import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createRoute } from "@tanstack/solid-router";
import { type Component, type ComponentProps, createMemo, For, Show } from "solid-js";
import { fromActorRef } from "@xstate/solid";
import {
  getApiStatusCategory,
  getApiStatusLabelKey,
  getSyncStatusCategory,
  getSyncStatusLabelKey,
} from "@bso/shared";
import { Info, RefreshCw, Server, UserCircle, Wifi, WifiOff } from "lucide-solid";

import { POWERSYNC_URL, SERVER_URL } from "@/lib/runtime-config";
import { appRoute } from "./route";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/ui/page-container";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

function DiagnosticRow(props: {
  label: string;
  value: string;
  status: "success" | "warning" | "danger" | "neutral";
  icon: Component<ComponentProps<"svg"> & { size?: number | string }>;
}) {
  const getStatusColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500 bg-green-500/10";
      }
      case "warning": {
        return "text-yellow-500 bg-yellow-500/10";
      }
      case "danger": {
        return "text-red-500 bg-red-500/10";
      }
      case "neutral": {
        return "text-muted-foreground bg-muted/10";
      }
      default: {
        return "text-muted-foreground bg-muted/10";
      }
    }
  };

  const getTextColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500";
      }
      case "warning": {
        return "text-yellow-500";
      }
      case "danger": {
        return "text-red-500";
      }
      case "neutral": {
        return "text-muted-foreground";
      }
      default: {
        return "text-muted-foreground";
      }
    }
  };

  return (
    <div class="flex items-center justify-between gap-4 py-2">
      <div class="flex items-center gap-3">
        <div
          class={`flex h-8 w-8 items-center justify-center rounded-full ${getStatusColorClass()}`}
        >
          <props.icon size={16} />
        </div>
        <span class="text-sm font-medium">{props.label}</span>
      </div>
      <span class={`text-sm font-bold shrink text-right truncate ${getTextColorClass()}`}>
        {props.value}
      </span>
    </div>
  );
}

function DebugPage() {
  const { connectivityRef } = usePowerSyncContext();
  const state = fromActorRef(connectivityRef);
  const { t } = useI18n();

  const context = createMemo(() => state().context);
  const stateValue = createMemo(() => state().value);

  const isRetryInProgress = createMemo(
    () => stateValue() === "checking" || stateValue() === "manualChecking",
  );

  const handleForceRetry = () => {
    connectivityRef.send({ type: "RETRY" });
  };

  const getApiLabel = () => t(getApiStatusLabelKey((v) => state().matches(v), context().apiStatus));
  const getApiState = () => getApiStatusCategory((v) => state().matches(v), context().apiStatus);
  const getSyncLabel = () =>
    t(getSyncStatusLabelKey((v) => state().matches(v), context().syncStatus));
  const getSyncState = () => getSyncStatusCategory((v) => state().matches(v), context().syncStatus);

  const canRetry = createMemo(() => state().can({ type: "RETRY" }));

  const systemItems: { label: MessageDescriptor; value: string }[] = [
    // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
    { label: msg`Adapter`, value: "WASM + OPFS" },
    { label: msg`Server URL`, value: SERVER_URL },
    { label: msg`PowerSync URL`, value: POWERSYNC_URL },
    { label: msg`Mode`, value: import.meta.env.DEV ? "development" : "production" },
  ];

  return (
    <div class="flex flex-1 flex-col overflow-hidden">
      <PageSubHeader title={t(msg`Diagnostics`)} />

      <PageContainer maxWidth="lg" class="space-y-6" containerClass="min-h-0">
        <div class="space-y-6">
          <GlassSurface class="p-4">
            <h2 class="mb-4 text-lg font-semibold">{t(msg`Connectivity`)}</h2>
            <div class="space-y-1">
              <DiagnosticRow
                label={t(msg`Internet Access`)}
                value={context().isOnline ? t(msg`Connected`) : t(msg`Disconnected`)}
                status={context().isOnline ? "success" : "danger"}
                icon={context().isOnline ? Wifi : WifiOff}
              />
              <DiagnosticRow
                label={t(msg`User Session`)}
                value={context().isAuthenticated ? t(msg`Active`) : t(msg`None`)}
                status={context().isAuthenticated ? "success" : "warning"}
                icon={UserCircle}
              />
              <DiagnosticRow
                label={t(msg`API Server (Writes)`)}
                value={getApiLabel()}
                status={getApiState()}
                icon={Server}
              />
              <Show when={context().serverVersion}>
                {(version) => (
                  <DiagnosticRow
                    label={t(msg`Server Version`)}
                    value={version()}
                    status="neutral"
                    icon={Info}
                  />
                )}
              </Show>
              <DiagnosticRow
                label={t(msg`PowerSync (Sync)`)}
                value={getSyncLabel()}
                status={getSyncState()}
                icon={RefreshCw}
              />
            </div>

            <Button
              variant="default"
              class="mt-6 w-full gap-2"
              onClick={handleForceRetry}
              disabled={isRetryInProgress() || !canRetry()}
            >
              <RefreshCw size={18} class={isRetryInProgress() ? "animate-spin" : ""} />
              {t(msg`Force connection retry`)}
            </Button>
          </GlassSurface>

          <Card>
            <CardHeader>
              <CardTitle>{t(msg`System Information`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div class="space-y-3">
                <For each={systemItems}>
                  {(item) => (
                    <>
                      <div class="flex items-center justify-between text-sm">
                        <span class="text-muted-foreground">{t(item.label)}</span>
                        <Badge variant="outline" class="font-mono text-xs">
                          {item.value}
                        </Badge>
                      </div>
                      <Separator />
                    </>
                  )}
                </For>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </div>
  );
}

export const debugRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/debug",
  component: DebugPage,
});
